/**
 * Alert box.
 *
 * The alert container is the `<dialog>` rendered by masterpage.xslt (`#alert-box`): it is opened
 * with `showModal()` — so that the browser handles the backdrop, the focus trap and the Escape
 * key — and closed with `close()`.
 *
 * Display duration follows the same logic of the modern implementation found in
 * `common/src/lib/utils/errors.ts`:
 *      - "fixed": the `messageBoxDuration` global (1500 ms when it is not set);
 *      - "adaptive" (default): the estimated read time of the message, never below the minimum
 *        defined for its type (see `getAlertDurationMinimum`) nor below `messageBoxDuration`,
 *        when that global is set to a number;
 *      - number: the box lasts exactly that many milliseconds (0 keeps it open until closed).
 * Hovering the box pauses the countdown, leaving it resumes it.
 */

var alertDurationMinimums = {
    danger: 8000,
    warning: 6000,
    success: 4000,
    info: 5000,
};

/**
 * Returns the minimum duration (in ms) of an alert of the given type.
 * @param {string} type The alert type
 * @returns {number} The minimum duration for the given alert type
 */
function getAlertDurationMinimum(type) {
    return alertDurationMinimums[type] || 5000;
}

/**
 * Rough estimate of the time (in ms) required to read `message`.
 * @param {string} message Message of which to estimate the read-time
 * @param {number} [minimumTime] Returned time will never be lower than this value (1500 by default)
 * @param {number} [baseTime] Base time scaling with the amount of words (the `messageBoxBaseReadTime` global, 1000 by default)
 * @returns {number} Estimated time (in ms)
 */
function estimateTextReadTime(message, minimumTime = 1500, baseTime = 0) {
    const avg_wps = 250 / 60;
    const words = String(message ?? "")
        .replace(/<[^>]*>/g, " ")
        .split(/\s+/)
        .filter((word) => word.length > 0).length;

    if (typeof minimumTime !== "number" || minimumTime <= 0) {
        minimumTime = 1500;
    }

    if (typeof baseTime !== "number" || baseTime <= 0) {
        baseTime =
            typeof window.messageBoxBaseReadTime === "number" &&
            window.messageBoxBaseReadTime > 0
                ? window.messageBoxBaseReadTime
                : 1000;
    }

    return Math.max(
        (minimumTime * baseTime) / 1000,
        (words / avg_wps) * baseTime
    );
}

/**
 * Returns the display duration of an alert, based on its type and on its message length.
 * @param {string} type The alert type
 * @param {string} message The message displayed by the alert
 * @returns {number} The calculated duration (in ms)
 */
function getAlertDuration(type, message) {
    return estimateTextReadTime(message, getAlertDurationMinimum(type));
}

/**
 * Resolves the display duration of a messagebox.
 * @param {string} type The alert type
 * @param {string} message The message displayed by the alert
 * @param {("fixed"|"adaptive"|number)} [duration] "fixed", "adaptive" (default) or an explicit amount of milliseconds (0 keeps the box open)
 * @returns {number} The resolved duration (in ms)
 */
function getMessageBoxDuration(type, message, duration) {
    const globalDuration = window.messageBoxDuration;
    const hasGlobalDuration =
        typeof globalDuration === "number" && globalDuration >= 0;

    if (typeof duration === "number" && duration >= 0) {
        return duration;
    }

    if (duration === "fixed") {
        return hasGlobalDuration ? globalDuration : 1500;
    }

    return estimateTextReadTime(
        message,
        hasGlobalDuration ? globalDuration : getAlertDurationMinimum(type)
    );
}

/**
 * @returns {?HTMLElement} The alert box container
 */
function getAlertBox() {
    return document.getElementById("alert-box") || $(".alert-box")[0] || null;
}

/**
 * Builds the markup of the alert box content.
 * @param {string} type Alert type: defines the `.alert-{type}` class and the alert image
 * @param {string} inner HTML of the message
 * @param {object} [options]
 * @param {string} [options.closeButtonId] Id to give to the close button
 * @param {boolean} [options.question] Adds the `question` class to the container
 * @param {string} [options.buttons] HTML of the buttons displayed under the message
 * @returns {string}
 */
function buildAlertMarkup(type, inner, options = {}) {
    const closeButton = options.closeButtonId
        ? `<img id="${options.closeButtonId}" class="close-sample" alt="Close" src="/skins/current-skin/images/icons/cancel.svg"></img>`
        : `<img class="close-error close-sample" alt="Close" src="/skins/current-skin/images/icons/cancel.svg"></img>`;

    return `
        <div class="alert-container ${
            options.question ? "question " : ""
        }fade in relative">
            <div class="alert-${type}">
                ${closeButton}
                <img class="alert-image" alt="Alert" src="/skins/current-skin/images/icons/${type}.svg"></img>
                <div class="alert">${inner}</div>
                ${options.buttons || ""}
            </div>
        </div>`;
}

/**
 * Opens the alert box with the given content, replacing the one it may already display.
 * @param {string} markup HTML of the `.alert-container` to display
 * @param {object} [options]
 * @param {string} [options.type] Alert type, used to compute the duration ("info" by default)
 * @param {string} [options.message] Message the duration is computed from (HTML tags are ignored)
 * @param {("fixed"|"adaptive"|number)} [options.duration] See `getMessageBoxDuration` ("adaptive" by default)
 * @param {string} [options.role] ARIA role of the box ("alert" by default)
 * @param {boolean} [options.closeOnBackdropClick=true] Close the box when its backdrop is clicked
 * @param {function} [options.onClose] Called when the box closes, however it got closed
 * @param {function} [options.onReady] Called with the box element and its `close` function, right after its content has been injected
 * @returns {?{ element: HTMLElement, close: function }}
 */
function openAlertBox(markup, options = {}) {
    const alertBox = getAlertBox();

    if (!alertBox) {
        return null;
    }

    const type = options.type || "info";
    const onClose =
        typeof options.onClose === "function" ? options.onClose : () => {};
    const duration = getMessageBoxDuration(
        type,
        options.message ?? "",
        options.duration
    );
    const isDialog =
        alertBox.tagName.toLowerCase() === "dialog" &&
        typeof alertBox.showModal === "function";
    const closeOnBackdrop = options.closeOnBackdropClick !== false;

    let closeTimer = null;
    let hasBeenClosed = false;
    let backdropPressed = false;

    function clearCloseTimer() {
        if (closeTimer) {
            clearTimeout(closeTimer);
            closeTimer = null;
        }
    }

    function setCloseTimer() {
        clearCloseTimer();

        if (duration > 0) {
            closeTimer = setTimeout(closeAlertBox, duration);
        }
    }

    function handleBackdropMouseDown(event) {
        backdropPressed = event.target === alertBox;
    }

    function handleBackdropClick(event) {
        // The mouse button has to be pressed on the backdrop too: otherwise selecting
        // the text of a message and releasing the button outside of it closes the box
        if (event.target === alertBox && backdropPressed) {
            closeAlertBox();
        }

        backdropPressed = false;
    }

    function handleCloseEvent() {
        cleanup();
    }

    function handleMouseEnter() {
        clearCloseTimer();
    }

    function handleMouseLeave() {
        setCloseTimer();
    }

    function cleanup() {
        if (hasBeenClosed) {
            return;
        }

        hasBeenClosed = true;
        clearCloseTimer();
        alertBox.removeEventListener("close", handleCloseEvent);
        alertBox.removeEventListener("mousedown", handleBackdropMouseDown);
        alertBox.removeEventListener("click", handleBackdropClick);
        alertBox.innerHTML = "";

        if (!isDialog) {
            alertBox.style.display = "none";
        }

        restorePageScroll();
        onClose();
    }

    function closeAlertBox() {
        if (isDialog && alertBox.open) {
            // The "close" event takes care of the cleanup
            alertBox.close();
        }
        else {
            cleanup();
        }
    }

    function display() {
        hasBeenClosed = false;
        backdropPressed = false;
        alertBox.innerHTML = markup;
        alertBox.setAttribute("role", options.role || "alert");

        if (options.role === "alertdialog") {
            alertBox.removeAttribute("aria-live");
            alertBox.removeAttribute("aria-atomic");
        }
        else {
            alertBox.setAttribute("aria-live", "assertive");
            alertBox.setAttribute("aria-atomic", "true");
        }

        blockPageScroll();

        if (isDialog) {
            alertBox.addEventListener("close", handleCloseEvent);

            if (closeOnBackdrop) {
                alertBox.addEventListener("mousedown", handleBackdropMouseDown);
                alertBox.addEventListener("click", handleBackdropClick);
            }

            alertBox.showModal();
        }
        else {
            alertBox.style.display = "flex";
        }

        if (typeof options.onReady === "function") {
            options.onReady(alertBox, closeAlertBox);
        }

        if (duration > 0 && alertBox.firstElementChild) {
            alertBox.firstElementChild.addEventListener(
                "mouseenter",
                handleMouseEnter
            );
            alertBox.firstElementChild.addEventListener(
                "mouseleave",
                handleMouseLeave
            );
        }

        setCloseTimer();
    }

    if (isDialog && alertBox.open) {
        // Another message is being displayed: wait for it to close before showing this one
        alertBox.addEventListener(
            "close",
            () => setTimeout(display, 0),
            { once: true }
        );
        alertBox.close();
    }
    else {
        display();
    }

    return {
        element: alertBox,
        close: closeAlertBox,
    };
}

/**
 * Show messagebox of type "type", displaying "message".
 * @param {("info"|"warning"|"success"|"danger")} type Type of the messagebox
 * @param {string} message Message to display
 * @param {?object} extra Object with a `content` property to display under the message
 * @param {() => void} [onClose] Function to call when the messagebox closes
 * @param {("fixed"|"adaptive"|number)} [duration] See `getMessageBoxDuration` ("adaptive" by default)
 */
function showMessageBox(type, message, extra, onClose = () => {}, duration = "adaptive") {
    if (typeof extra === "function") {
        onClose = extra;
        extra = null;
    }

    const content = extra ? `<div>${extra.content ?? ""}</div>` : "";
    const markup = buildAlertMarkup(type, message + content, {
        closeButtonId: "close-error",
    });

    return openAlertBox(markup, {
        type,
        message,
        duration,
        onClose,
        onReady: (alertBox, close) => {
            const closeButton = alertBox.querySelector("#close-error");

            if (closeButton) {
                closeButton.addEventListener("click", close);
            }
        },
    });
}

/**
 * Show a messagebox which can display multi-line messages.
 * @param {("info"|"warning"|"success"|"danger")} type Type of the messagebox
 * @param {string} message Message to display: every line becomes a paragraph
 * @param {("fixed"|"adaptive"|number)} [duration] See `getMessageBoxDuration` ("adaptive" by default)
 */
function showLargeMessageBox(type, message, duration = "adaptive") {
    const paragraphs = String(message ?? "")
        .split("\n")
        .map((part) => `<p>${part}</p>`)
        .join("");
    const markup = buildAlertMarkup(type, paragraphs, {
        closeButtonId: "close-error",
    });

    return openAlertBox(markup, {
        type,
        message,
        duration,
        onReady: (alertBox, close) => {
            const closeButton = alertBox.querySelector("#close-error");

            if (closeButton) {
                closeButton.addEventListener("click", close);
            }
        },
    });
}

/**
 * Opens a dialog prompting the user to confirm some action.
 * @param {string} message Message to display
 * @param {?object} extra Object with a `content` property to display under the message
 * @param {() => void} onConfirm Function to call if the confirm button is clicked
 * @param {() => void} [onClose] Function to call if the box is closed or the cancel button is clicked
 */
function showQuestionBox(message, extra, onConfirm, onClose = () => {}) {
    const content = extra ? `<div>${extra.content ?? ""}</div>` : "";
    const buttons = `
                <div class="flex mt-16">
                    <input type="button" class="btn btn-secondary width-100 close-button" value="${buttons_close}">
                    <input type="button" class="btn btn-primary width-100 confirm-button" value="${buttons_confirm}">
                </div>`;
    const markup = buildAlertMarkup("danger", message + content, {
        question: true,
        buttons,
    });

    let confirmed = false;

    return openAlertBox(markup, {
        type: "danger",
        role: "alertdialog",
        duration: 0,
        onClose: () => {
            if (!confirmed) {
                onClose();
            }
        },
        onReady: (alertBox, close) => {
            const confirmButton = alertBox.querySelector(".confirm-button");

            if (confirmButton) {
                confirmButton.addEventListener("click", () => {
                    confirmed = true;
                    onConfirm();
                    close();
                });
            }

            alertBox
                .querySelectorAll(".close-error, .close-button")
                .forEach((cancelButton) => {
                    cancelButton.addEventListener("click", close);
                });
        },
    });
}

/**
 * Opens the alert box with the content rendered server-side by the `alert-content` template
 * (masterpage.xslt), if it has any.
 *
 * Opening the box also pushes the `app_alert` GTM event, mirroring the modern implementation in
 * `common/ftl/main.ftl`.
 */
function showStaticAlertBox() {
    const alertBox = getAlertBox();

    if (!alertBox || alertBox.open) {
        return;
    }

    const messageBox = alertBox.querySelector(".alert-container");

    if (!messageBox) {
        return;
    }

    const alertElement = messageBox.querySelector(".alert-danger, .alert-success, .alert-warning, .alert-info");
    const type = alertElement ? alertElement.className.match(/alert-(\w+)/)[1] : "danger";
    const message = (messageBox.querySelector(".alert") || messageBox).textContent;
    const alertCode = getUrlParam("errorMessage") || getUrlParam("errorCode") || getUrlParam("error") || "";

    openAlertBox(messageBox.outerHTML, {
        type,
        message,
        duration: getAlertDuration(type, message),
        onReady: (box, close) => {
            box.querySelectorAll(".close-sample").forEach((closeButton) => {
                closeButton.addEventListener("click", close);
            });

            pushGTMEvent({
                event: "app_alert",
                alert_code: alertCode,
                alert_message: message,
            });
        },
    });
}

$(function () {
    if (getUrlParam("error")) {
        showStaticAlertBox();
    }
});
